import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { OfflineOperation } from 'src/app/services/offline/offline-storage.service';
import { OfflineStorageService } from 'src/app/services/offline/offline-storage.service';
import { StoreForwardOperacionesGatewayApi } from 'src/core/apis/store-forward.operaciones';
import { TransportistaSessionStorage } from 'src/core/jwt/session.storage';
import { DeviceIdService } from './device-id.service';
import { SessionIdService } from './session-id.service';
import { resolveSistemaOperativo } from './sistema-operativo.util';
import {
  buildAttemptRequest,
  buildOperationRequest,
  pickPedidoFromOp,
  pickTicketFromOp,
} from './store-forward.mapper';
import {
  StoreForwardAttemptRequest,
  StoreForwardAttemptResult,
  StoreForwardOperationRequest,
  StoreForwardOperationState,
} from './store-forward.types';
import { randomUuid } from './uuid.util';

@Injectable({ providedIn: 'root' })
export class StoreForwardMonitoringService {
  constructor(
    private readonly api: StoreForwardOperacionesGatewayApi,
    private readonly deviceId: DeviceIdService,
    private readonly sessionId: SessionIdService,
    private readonly gatewaySession: TransportistaSessionStorage,
    private readonly offlineStorage: OfflineStorageService
  ) {}

  isEnabled(): boolean {
    return environment.logsEnabled && !!environment.logsAppKey;
  }

  /** Registra operación PENDIENTE al encolar localmente. */
  registerPendingOperation(op: OfflineOperation, operationId: string): void {
    if (!this.isEnabled()) {
      return;
    }
    const body = buildOperationRequest(op, operationId, this.enrichContext({
      estado: 'PENDIENTE',
      fechaCreacionLocal: new Date(op.queuedAt ?? Date.now()).toISOString(),
      correlationId: op.correlationId,
      datosAdicionales: {
        offlineOpType: op.type,
        sessionId: this.sessionId.getSessionId(),
      },
    }));
    void this.safeRegisterOperation(body);
  }

  /** Actualiza estado consolidado (REINTENTANDO, ENVIADO, ERROR_PERMANENTE). */
  updateOperationState(
    op: OfflineOperation,
    operationId: string,
    estado: StoreForwardOperationState,
    extra?: Partial<StoreForwardOperationRequest>
  ): void {
    if (!this.isEnabled()) {
      return;
    }
    const body = buildOperationRequest(op, operationId, this.enrichContext({
      estado,
      correlationId: op.correlationId,
      cantidadIntentos: (op.retryCount ?? 0) + (estado === 'REINTENTANDO' ? 1 : 0),
      fechaUltimoIntento: new Date().toISOString(),
      fechaEnvioExitoso: estado === 'ENVIADO' ? new Date().toISOString() : undefined,
      ...extra,
    }));
    void this.safeRegisterOperation(body);
  }

  /** Registra un intento de envío con attemptId único. */
  registerAttempt(
    op: OfflineOperation,
    operationId: string,
    attemptId: string,
    numeroIntento: number,
    resultado: StoreForwardAttemptResult,
    partial?: Partial<StoreForwardAttemptRequest>
  ): void {
    if (!this.isEnabled()) {
      return;
    }
    const body = buildAttemptRequest(attemptId, operationId, numeroIntento, {
      resultado,
      estadoConectividad: this.resolveConnectivity(),
      ...partial,
    });
    void this.safeRegisterAttempt(body);
  }

  createAttemptId(): string {
    return randomUuid();
  }

  private enrichContext(
    partial: Partial<StoreForwardOperationRequest>
  ): Partial<StoreForwardOperationRequest> {
    return {
      dispositivoId: this.deviceId.getDeviceId(),
      usuarioId: this.resolveUsuarioId() ?? undefined,
      rolUsuario: this.resolveRolUsuario() ?? undefined,
      versionAplicacion: environment.appVersion,
      sistemaOperativo: resolveSistemaOperativo(),
      estadoConectividad: this.resolveConnectivity(),
      ...partial,
    };
  }

  private async safeRegisterOperation(body: StoreForwardOperationRequest): Promise<void> {
    try {
      await this.api.registerOperation(body).toPromise();
    } catch (err) {
      if (!environment.production) {
        console.warn('[StoreForwardMonitoring] registerOperation failed', err);
      }
    }
  }

  private async safeRegisterAttempt(body: StoreForwardAttemptRequest): Promise<void> {
    try {
      await this.api.registerAttempt(body).toPromise();
    } catch (err) {
      if (!environment.production) {
        console.warn('[StoreForwardMonitoring] registerAttempt failed', err);
      }
    }
  }

  private resolveUsuarioId(): string | null {
    try {
      const gw = this.gatewaySession.getUser<{ RUT_M?: string; RUT_USUARIO?: string }>();
      if (gw?.RUT_M) {
        return String(gw.RUT_M);
      }
      if (gw?.RUT_USUARIO) {
        return String(gw.RUT_USUARIO);
      }
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.RUT_USUARIO ? String(u.RUT_USUARIO) : null;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private resolveRolUsuario(): string | null {
    try {
      const gw = this.gatewaySession.getUser<{ ROL?: string }>();
      if (gw?.ROL) {
        return String(gw.ROL);
      }
      const raw = localStorage.getItem('user');
      if (raw) {
        const u = JSON.parse(raw);
        return u?.ROL ? String(u.ROL) : null;
      }
    } catch {
      /* ignore */
    }
    return null;
  }

  private resolveConnectivity(): 'ONLINE' | 'OFFLINE' {
    return this.offlineStorage.isOnline() ? 'ONLINE' : 'OFFLINE';
  }

  logContextFromOp(op: OfflineOperation): {
    operationId: string;
    correlationId?: string;
    tkt?: string;
    pedido?: string;
  } {
    return {
      operationId: op.operationId ?? randomUuid(),
      correlationId: op.correlationId,
      tkt: pickTicketFromOp(op),
      pedido: pickPedidoFromOp(op),
    };
  }
}
